import { useCallback } from 'react';
import type { User } from 'oidc-client-ts';
import {
  authenticatedFetchWithTenantAnalytics,
  useAnalytics,
  useEnsureLiveSession,
  useTenant,
  willAuthenticate
} from '@parcel2go/npm-p2g-app';
import { ApiResponse } from '~/lib/api/api-response';
import { fetchBackgroundTaskJson } from './background-task-config';

/** Sentry tag for a failed renewal, as `ensureLiveSession` reports it. */
const SECTION = 'background-task';

type EnsureLiveSession = ReturnType<typeof useEnsureLiveSession>;

/**
 * The four notification tasks poll together, so when an idle tab resumes they all find the same
 * lapsed token at once. One renewal serves all of them rather than four silent sign-ins racing.
 */
let inFlightRenewal: Promise<User | null | undefined> | null = null;

function renewSession(ensureLiveSession: EnsureLiveSession) {
  if (!inFlightRenewal) {
    inFlightRenewal = ensureLiveSession({ section: SECTION }).finally(() => {
      inFlightRenewal = null;
    });
  }
  return inFlightRenewal;
}

/**
 * Fetcher for the notification background tasks (claims, support, surcharges, refunds).
 *
 * After a tab has sat idle past `expires_at`, `authenticatedFetchWithTenantAnalytics` silently
 * drops the bearer, so every poll went out anonymously and came back as an empty-bodied 401. This
 * renews the session first and sends the request with the renewed user. If the session cannot be
 * renewed there is no identity to poll with, so nothing is sent and the task shows no data - the
 * same outcome the 401 produced, without the request.
 */
export default function useBackgroundTaskFetch() {
  const { session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const ensureLiveSession = useEnsureLiveSession();

  return useCallback(
    async <T>(url: string, taskName: string): Promise<ApiResponse<T> | null> => {
      const user = await renewSession(ensureLiveSession);
      if (!willAuthenticate(user)) {
        return null;
      }

      return fetchBackgroundTaskJson<T>(
        authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, url),
        taskName
      );
    },
    [ensureLiveSession, tenant, settings, session]
  );
}
